// apps/web/src/components/AnnouncementLoginCarousel.tsx
//
// ESCR-269.5 (R2) — Carrossel de login. Um único modal pós-login que apresenta
// em sequência os comunicados de destaque elegíveis (GET /announcements/active-popup
// = LISTA ORDENADA, cap 5). Indicador "X de N", Próximo/Anterior, dots, Pular.
// Marca seen dos `uma_vez` ao ver cada slide / pular / fechar. 1 modal por sessão
// (sessionStorage). Renderizado dentro do AppLayout (após AuthGuard).

import { useEffect, useMemo, useRef, useState } from 'react';

import { ArrowLeft, ArrowRight, ImageOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useActiveAnnouncementPopup, useMarkAnnouncementRead } from '@alamo/api';
import type { Announcement } from '@alamo/types';
import { Dialog, DialogContent, DialogTitle, cn } from '@alamo/ui';

import { ANNOUNCEMENT_KIND_META } from '../pages/Announcements/announcementKind';
import { AnnouncementSummary } from '../pages/Announcements/components/AnnouncementSummary';
import { SanitizedHtml } from '../pages/Announcements/components/SanitizedHtml';

const SESSION_KEY = 'alamo_announcement_modal_shown';

export function AnnouncementLoginCarousel() {
  const { t } = useTranslation('consultor');
  const navigate = useNavigate();
  const { data } = useActiveAnnouncementPopup();
  const markRead = useMarkAnnouncementRead();

  const items = useMemo<Announcement[]>(() => data ?? [], [data]);

  const [open, setOpen] = useState(false);
  const [index, setIndex] = useState(0);
  const seenRef = useRef<Set<string>>(new Set());

  // Abre 1x por sessão quando há itens elegíveis.
  useEffect(() => {
    if (items.length === 0) return;
    if (sessionStorage.getItem(SESSION_KEY) === 'true') return;
    setOpen(true);
    sessionStorage.setItem(SESSION_KEY, 'true');
  }, [items.length]);

  const current = items[index];

  // Marca seen dos `uma_vez` ao ver cada slide (idempotente por sessão).
  useEffect(() => {
    if (!open || !current) return;
    if (current.displayFrequency !== 'uma_vez') return;
    if (seenRef.current.has(current.id)) return;
    seenRef.current.add(current.id);
    markRead.mutate(current.id);
  }, [open, current, markRead]);

  if (!current) return null;

  const total = items.length;
  const meta = ANNOUNCEMENT_KIND_META[current.kind];
  const KindIcon = meta.icon;
  // Capa 16:9; se ausente, cai pro banner 16:5 (squareImage) para não exibir placeholder
  // quando há ≥1 imagem. object-cover crop nas laterais.
  const coverImage = current.coverImage ?? current.squareImage;

  const goPrev = () => setIndex((i) => Math.max(0, i - 1));
  const goNext = () => setIndex((i) => Math.min(total - 1, i + 1));
  const close = () => setOpen(false);

  const handleCta = () => {
    close();
    navigate(`/novidades/${current.id}`);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? null : close())}>
      <DialogContent className="max-w-lg overflow-hidden p-0">
        <DialogTitle className="sr-only">{t('novidades.popup.title')}</DialogTitle>

        {/* Capa */}
        <div className="relative aspect-video w-full bg-slate-100">
          {coverImage ? (
            <img src={coverImage.url} alt="" className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full w-full items-center justify-center">
              <ImageOff className="h-10 w-10 text-slate-300" aria-hidden />
            </div>
          )}
        </div>

        {/* Conteúdo */}
        <div className="space-y-3 p-5">
          <div className="flex items-center justify-between gap-2">
            <span
              className={cn(
                'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-body font-semibold',
                meta.chipClass,
              )}
            >
              <KindIcon className="h-3.5 w-3.5" aria-hidden />
              {t(meta.labelKey)}
            </span>
            {total > 1 && (
              <span className="text-xs font-body text-slate-500">
                {t('novidades.popup.counter', { current: index + 1, total })}
              </span>
            )}
          </div>

          <h2 className="font-heading text-xl font-bold text-secondary-950">{current.title}</h2>
          <AnnouncementSummary
            html={current.summary}
            className="text-sm font-body text-slate-600"
          />
          <SanitizedHtml html={current.body} className="max-h-40 overflow-auto" />
        </div>

        {/* Footer nav */}
        <div className="flex items-center justify-between gap-2 border-t border-slate-200 p-4">
          <div className="flex items-center gap-2">
            {total > 1 && (
              <>
                <button
                  type="button"
                  onClick={goPrev}
                  disabled={index === 0}
                  className="flex min-h-11 items-center gap-1 rounded-md px-2 text-sm font-body text-slate-600 disabled:opacity-40"
                >
                  <ArrowLeft className="h-4 w-4" aria-hidden />
                  {t('novidades.popup.prev')}
                </button>
                <div className="flex items-center gap-1" aria-hidden>
                  {items.map((it, i) => (
                    <span
                      key={it.id}
                      className={cn(
                        'h-1.5 w-1.5 rounded-full',
                        i === index ? 'bg-laranja-encanto' : 'bg-slate-300',
                      )}
                    />
                  ))}
                </div>
              </>
            )}
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={close}
              className="min-h-11 rounded-md px-3 text-sm font-body text-slate-500 hover:text-slate-700"
            >
              {t('novidades.popup.skip')}
            </button>
            {index < total - 1 ? (
              <button
                type="button"
                onClick={goNext}
                data-testid="announcement-popup-next"
                className="flex min-h-11 items-center gap-1 rounded-lg bg-primary-500 px-4 text-sm font-body font-medium text-white hover:bg-primary-600"
              >
                {t('novidades.popup.next')}
                <ArrowRight className="h-4 w-4" aria-hidden />
              </button>
            ) : (
              <button
                type="button"
                onClick={handleCta}
                className="flex min-h-11 items-center gap-1 rounded-lg bg-primary-500 px-4 text-sm font-body font-medium text-white hover:bg-primary-600"
              >
                {current.ctaText || t('novidades.cta_default')}
                <ArrowRight className="h-4 w-4" aria-hidden />
              </button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
